/**
 * Pick an app.
 *
 * Shared by the Distribution and Codes pages: search, then choose a view — grid (the
 * default), table, or list. Selecting an app opens its own page rather than swapping a
 * dropdown, so the URL is shareable and the back button does what you expect.
 *
 * Each tile can carry a status line (pool counts, whether distribution is set up), which
 * is what makes the grid worth looking at rather than just a list of names.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  IconSearch,
  IconLayoutGrid,
  IconTable,
  IconList,
  IconMoodEmpty,
  IconChevronRight,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Text,
  TextInput,
  Paper,
  SimpleGrid,
  Avatar,
  Table,
  Badge,
} from '@/components/ui';
import { SegmentedControl } from '@mantine/core';
import { HoverLift } from '@/components/animations';
import type { AppManifest } from '@actschurch/ccms-sdk';

export type AppBrowserView = 'grid' | 'table' | 'list';

export interface AppBrowserItem {
  app: AppManifest;
  /** Short status line under the name, e.g. "12 available · 3 used". */
  summary?: ReactNode;
  /** A chip on the right, e.g. "Not set up" / "Live". */
  badge?: { label: string; color: string };
}

interface AppBrowserProps {
  items: AppBrowserItem[];
  onSelect: (appId: string) => void;
  /** Shown when the search matches nothing, or there are no apps at all. */
  emptyMessage?: string;
  searchPlaceholder?: string;
}

export default function AppBrowser({
  items,
  onSelect,
  emptyMessage = 'No apps match your search.',
  searchPlaceholder = 'Search apps…',
}: AppBrowserProps) {
  const [view, setView] = useState<AppBrowserView>('grid');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(
      ({ app }) =>
        app.displayName.toLowerCase().includes(q) ||
        app.appId.toLowerCase().includes(q) ||
        (app.description ?? '').toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <Stack gap="md">
      <Group>
        <TextInput
          placeholder={searchPlaceholder}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <SegmentedControl
          value={view}
          onChange={(v) => setView(v as AppBrowserView)}
          size="sm"
          data={[
            { value: 'grid', label: <IconLayoutGrid size={16} /> },
            { value: 'table', label: <IconTable size={16} /> },
            { value: 'list', label: <IconList size={16} /> },
          ]}
        />
      </Group>

      {rows.length === 0 ? (
        <Paper withBorder p="xl" ta="center">
          <Stack gap="xs" align="center">
            <IconMoodEmpty size={32} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed">{emptyMessage}</Text>
          </Stack>
        </Paper>
      ) : view === 'grid' ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="md">
          {rows.map((item) => (
            <GridTile key={item.app.appId} item={item} onSelect={onSelect} />
          ))}
        </SimpleGrid>
      ) : view === 'table' ? (
        <div style={{ overflowX: 'auto' }}>
          <Table verticalSpacing="sm" style={{ minWidth: 640 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>App</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(({ app, summary, badge }) => (
                <Table.Tr
                  key={app.appId}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelect(app.appId)}
                >
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar
                        src={app.iconUrl || undefined}
                        radius="md"
                        size="sm"
                        style={{ backgroundColor: app.colors?.primary || undefined }}
                      >
                        {app.shortName?.charAt(0) ?? '?'}
                      </Avatar>
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} lineClamp={1}>
                          {app.displayName}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {app.appId}
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {badge && (
                        <Badge size="sm" color={badge.color} variant="light">
                          {badge.label}
                        </Badge>
                      )}
                      {summary && (
                        <Text size="xs" c="dimmed">
                          {summary}
                        </Text>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">
                    <IconChevronRight size={16} color="var(--mantine-color-gray-5)" />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      ) : (
        <Stack gap="xs">
          {rows.map(({ app, summary, badge }) => (
            <Card
              key={app.appId}
              withBorder
              padding="sm"
              radius="md"
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(app.appId)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Avatar
                    src={app.iconUrl || undefined}
                    radius="md"
                    size="md"
                    style={{ backgroundColor: app.colors?.primary || undefined }}
                  >
                    {app.shortName?.charAt(0) ?? '?'}
                  </Avatar>
                  <div style={{ minWidth: 0 }}>
                    <Text fw={600} lineClamp={1}>
                      {app.displayName}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {summary ?? app.appId}
                    </Text>
                  </div>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  {badge && (
                    <Badge size="sm" color={badge.color} variant="light">
                      {badge.label}
                    </Badge>
                  )}
                  <IconChevronRight size={16} color="var(--mantine-color-gray-5)" />
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function GridTile({ item, onSelect }: { item: AppBrowserItem; onSelect: (appId: string) => void }) {
  const { app, summary, badge } = item;
  return (
    <HoverLift lift={4}>
      <Card
        withBorder
        padding="md"
        radius="md"
        h="100%"
        style={{ cursor: 'pointer' }}
        onClick={() => onSelect(app.appId)}
      >
        <Stack gap="sm" h="100%">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Avatar
              src={app.iconUrl || undefined}
              radius="md"
              size="lg"
              style={{ backgroundColor: app.colors?.primary || undefined }}
            >
              {app.shortName?.charAt(0) ?? '?'}
            </Avatar>
            {badge && (
              <Badge size="sm" color={badge.color} variant="light">
                {badge.label}
              </Badge>
            )}
          </Group>

          <div>
            <Text fw={600} lineClamp={1}>
              {app.displayName}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {app.appId}
            </Text>
          </div>

          <div style={{ flex: 1 }} />

          {summary && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {summary}
            </Text>
          )}
        </Stack>
      </Card>
    </HoverLift>
  );
}
